<template>
    <div class="tracking-qr-scanner" :aria-busy="busy">
        <CameraCodeScanner
            ref="scanner"
            :formats="trackingFormats"
            :show-scan-frame="showScanFrame"
            idle-text="Camera stopped"
            @decoded="onDecoded"
            @scan-error="onCameraError"
        />

        <div class="tracking-qr-scanner__manual mt-4 pt-4 border-top">
            <div class="mb-2">
                <label class="form-label fw-semibold mb-1" :for="manualInputId">
                    Enter label code
                </label>
                <p class="text-muted small mb-0">
                    {{ manualHelpText }}
                </p>
            </div>
            <div class="input-group tracking-manual-controls">
                <input
                    :id="manualInputId"
                    v-model="manualValue"
                    class="form-control font-monospace"
                    type="text"
                    autocomplete="off"
                    :placeholder="manualPlaceholder"
                    :disabled="busy"
                    @keyup.enter="submitManual"
                />
                <button
                    class="btn forkto-primary-action"
                    type="button"
                    :disabled="busy || !manualValue.trim()"
                    @click="submitManual"
                >
                    {{ expectedType ? "Use ID" : "Identify intake" }}
                </button>
            </div>
        </div>

        <div v-if="errorMessage" class="alert alert-warning mt-3 mb-0">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
import { BarcodeFormat } from "@zxing/browser";

import CameraCodeScanner from "@/components/CameraCodeScanner.vue";
import { parseUniversalRecordInput } from "@/services/universalRecordIdentifier";

let trackingScannerInstance = 0;

export default {
    name: "TrackingQrScanner",
    components: { CameraCodeScanner },
    props: {
        expectedType: { type: String, default: "" },
        busy: { type: Boolean, default: false },
        showScanFrame: { type: Boolean, default: false }
    },
    emits: ["decoded", "scan-error"],
    expose: ["start", "stop", "setError"],
    data() {
        trackingScannerInstance += 1;
        return {
            manualInputId: `tracking-label-code-${trackingScannerInstance}`,
            trackingFormats: [BarcodeFormat.QR_CODE],
            manualValue: "",
            errorMessage: ""
        };
    },
    computed: {
        manualPlaceholder() {
            return this.expectedType
                ? "Scan or enter the Forkto record ID"
                : "Scan or enter the full Forkto record URN";
        },
        manualHelpText() {
            return this.expectedType
                ? "Use the intake ID printed below the QR code."
                : "Use the full identifier printed below the QR code.";
        },
        invalidLabelMessage() {
            return this.expectedType
                ? "This is not a valid Forkto record label. Scan the tracking QR or enter its intake ID."
                : "This is not a valid Forkto record label. Scan the tracking QR or enter the full Forkto record URN.";
        }
    },
    beforeUnmount() {
        this.stop();
    },
    methods: {
        setError(message, code = "unavailable") {
            this.errorMessage = message;
            this.$emit("scan-error", { code });
        },
        clearError() {
            this.errorMessage = "";
        },
        start() {
            this.clearError();
            return this.$refs.scanner?.start();
        },
        stop() {
            this.$refs.scanner?.stop();
        },
        onCameraError({ code }) {
            this.$emit("scan-error", { code });
        },
        onDecoded({ text }) {
            this.acceptValue(text, "camera");
        },
        submitManual() {
            if (this.busy || !this.manualValue.trim()) return;
            this.acceptValue(this.manualValue, "manual");
        },
        acceptValue(value, source) {
            try {
                const parsed = parseUniversalRecordInput(value, {
                    expectedType: this.expectedType,
                    allowBareId: source === "manual"
                });
                this.manualValue = parsed.urn;
                this.stop();
                this.$emit("decoded", { ...parsed, source });
            } catch (error) {
                const code = error?.code || "malformed_identifier";
                // Show what was actually read. Without it a rejected code is
                // indistinguishable from "the camera saw nothing", and the user
                // cannot tell a legacy URL label from a bad scan.
                const raw = String(value || "").trim();
                const preview = raw.length > 80 ? `${raw.slice(0, 77)}...` : raw;
                const message =
                    code === "unsupported_version"
                        ? "This label uses a newer Forkto identifier version. Update the app before scanning it."
                        : `${this.invalidLabelMessage} Scanned: "${preview}"`;
                this.setError(message, code);
                console.warn("[TrackingQrScanner] rejected code", { code, raw, source });
                // Do not restart the camera here. The rejected code is still
                // in frame, so an immediate restart re-decodes it within
                // ~100ms and loops with a beep on every pass. The error stays
                // up; the user retries deliberately.
            }
        }
    }
};
</script>

<style scoped>
@media (max-width: 575.98px) {
    .tracking-manual-controls {
        flex-direction: column;
        gap: 0.5rem;
    }

    .tracking-manual-controls > .form-control,
    .tracking-manual-controls > .btn {
        width: 100%;
        min-height: 44px;
        border-radius: var(--forkto-radius-control) !important;
    }
}
</style>
